import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import KeenSlider, {KeenSliderInstance} from 'keen-slider';
import {CustomPostTypeObjectNameEnum} from 'src/app/enums/WPEnums';
import {SearchResultsCareproviderUnitItem} from '../../types/SearchResultsCareproviderUnitItem';
import {WPPageCustom} from '../../types/WPPageCustom';
import {WPPostBlog} from '../../types/WPPostBlog';
import {WPPostGuide} from '../../types/WPPostGuide';
import {WPPostNative} from '../../types/WPPostNative';
import {ToolsService} from '../../services/tools.service';
import {WindowSizeService} from '../../services/window-size.service';
import {isPlatformBrowser} from '@angular/common';
import {DLPageTypeEnum} from '../../enums/DataLayerEnums';
import {NativePuffTypeEnum} from '../../enums/AdEnums';

@Component({
  selector: 'app-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarouselComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('sliderRef') sliderRef: ElementRef<HTMLElement>;

  @Input() title: string;
  @Input() puffs: Array<WPPostBlog | WPPostNative | WPPostGuide | WPPageCustom | SearchResultsCareproviderUnitItem>;
  @Input() puffType: NativePuffTypeEnum | string;
  @Input() adNamePreffix: string;
  @Input() qaHookPreffix: string;
  @Input() isPageWithSidebar: boolean = false;
  @Input() pageType: DLPageTypeEnum;

  public customPostTypeObjectNameEnum = CustomPostTypeObjectNameEnum;
  public slider: KeenSliderInstance = null;
  public currentSlide = signal<number>(1);
  public sliderPuffs: Array<
    WPPostBlog | WPPostNative | WPPostGuide | WPPageCustom | SearchResultsCareproviderUnitItem
  > = [];
  public numberOfPriorityPuffs = signal<number>(1);

  private windowSizeService = inject(WindowSizeService);
  private size = this.windowSizeService.windowSize;

  constructor(
    private tools: ToolsService,
    @Inject(PLATFORM_ID) private platformId: any,
    private ngZone: NgZone,
    private _change: ChangeDetectorRef,
  ) {
    // effect(
    //   () => {
    //     this.setNumberOfPrioritySlides(this.size() ? this.size()[0] : 0);
    //   },
    //   {
    //     allowSignalWrites: true,
    //   },
    // );
  }

  public ngAfterViewInit() {
    this.sliderPuffs = this.tools.clone(this.puffs);
    this.resetSlider();
    this.delayedSliderInit();
  }

  public ngOnChanges() {
    this.sliderPuffs = this.tools.clone(this.puffs);
    this.resetSlider();
    this.delayedSliderInit();
  }

  public ngOnDestroy() {
    this.slider = null;
  }

  private conditionalSliderInit(): void {
    if (isPlatformBrowser(this.platformId) && this.sliderRef?.nativeElement) {
      this.initSlider();
      this._change.markForCheck();
    }
  }

  private initSlider() {
    this.slider = new KeenSlider(
      this.sliderRef.nativeElement,
      {
        loop: true,
        slides: {
          perView: 1,
          spacing: 0,
        },
        breakpoints: {
          '(min-width: 576px)': {
            slides: {
              perView: 2,
              spacing: 16,
            },
          },
          '(min-width: 768px)': {
            slides: {
              perView: 3,
              spacing: 16,
            },
          },
          '(min-width: 992px)': {
            slides: {
              perView: this.isPageWithSidebar ? 2 : 3,
              spacing: 16,
            },
          },
          '(min-width: 1200px)': {
            slides: {
              perView: 3,
              spacing: 16,
            },
          },
        },
        initial: this.currentSlide(),
        slideChanged: (s) => this.currentSlide.set(s.track.details.rel),
      },
      [
        // add plugins here
        (slider) => {
          let timeout;
          let mouseOver = false;

          function clearNextTimeout() {
            clearTimeout(timeout);
          }

          function nextTimeout() {
            clearTimeout(timeout);
            if (mouseOver) return;
            timeout = setTimeout(() => {
              slider.next();
            }, 5000);
          }

          slider.on('created', () => {
            slider.container.addEventListener('mouseover', () => {
              mouseOver = true;
              clearNextTimeout();
            });
            slider.container.addEventListener('mouseout', () => {
              mouseOver = false;
              nextTimeout();
            });
            nextTimeout();
          });

          slider.on('dragStarted', clearNextTimeout);
          slider.on('animationEnded', nextTimeout);
          slider.on('updated', nextTimeout);
        },
      ],
    );
  }

  private resetSlider() {
    this.slider = null;
    this.currentSlide.set(1);
  }

  private setNumberOfPrioritySlides(width: number) {
    if (width < 576) {
      this.numberOfPriorityPuffs.set(1);
    } else if (width < 768) {
      this.numberOfPriorityPuffs.set(2);
    } else {
      this.numberOfPriorityPuffs.set(3);
    }
  }

  private delayedSliderInit() {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.ngZone.run(() => {
          this.conditionalSliderInit();
        });
      }, 0);
    });
  }
}
